import { computed, ComputedRef, toRef, toValue } from 'vue';

import { useLiveQueryV2, CODES } from '@libs/use';
import { OptionContractChain } from '@libs/api-reference';
import {
  SYMBOL_TRADE_CHANNEL_PREFIX,
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX,
  transformTradePriceChange,
  transformSymbolTradingStateChange,
  transformClosingPriceChange,
} from '@core/symbol';

import * as API from '../requests';
import { OPTION_QUERY_KEYS, CONTRACT_INFO_CHANGED_CHANNEL } from '../constants';
import { CalcSymbolValues } from '../utils/calculator';
import { transformOptionContractChanged } from '../utils/transform/optionContractChanged';

interface getContractChainProps {
  underlyingAssetIsin: ComputedRef<string> | string;
  endDate: ComputedRef<string> | string;
  contractType: ComputedRef<number> | number;
  immediate?: ComputedRef<boolean> | boolean;
}

export default function useContractsChain({
  contractType,
  endDate,
  underlyingAssetIsin,
  immediate = false,
}: getContractChainProps) {
  const {
    result,
    status: getContractsChainStatus,
    execute: getContractsChain,
    isPending: isLoadingContractsChain,
    isSuccess: isSuccessLoadingContractsChain,
    isError: isErrorLoadingContractsChain,
  } = useLiveQueryV2<OptionContractChain>({
    fetcher: () =>
      API.getContractsChain({
        contractType: toValue(contractType),
        endDate: toValue(endDate),
        underlyingAssetIsin: toValue(underlyingAssetIsin),
      }),
    key: OPTION_QUERY_KEYS.contractsChain(
      toRef({ contractType, endDate, underlyingAssetIsin })
    ),
    immediate,
    cacheTime: 1000 * 60, // 1m
    staleTime: 1000 * 60, // 1m,
    getChannels(result) {
      const chainDetailList = result?.data.contractChainDetails;

      return chainDetailList?.reduce(
        (chainChanels: string[], chainDetail) => {
          chainChanels.push(
            `${SYMBOL_TRADE_CHANNEL_PREFIX}${chainDetail.isin}`,
            `${SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX}${chainDetail.isin}`,
            `${SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX}${chainDetail.isin}`,
            `${CONTRACT_INFO_CHANGED_CHANNEL}${chainDetail.isin}`
          );

          return chainChanels;
        },
        [
          `${SYMBOL_TRADE_CHANNEL_PREFIX}${result?.data.underlyingInstrumentIsin}`,
        ]
      );
    },
    onUpdate(signal) {
      const actions = {
        [CODES.OptionContractChanged]: transformOptionContractChanged,
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.SymbolStateChangeSignal]: transformSymbolTradingStateChange,
        [CODES.TseInstrumentClosingPriceChangeSignal]:
          transformClosingPriceChange,
      };

      const chainDetails = result.value?.data.contractChainDetails;

      if (actions[signal.code] && result.value?.data) {
        const data: ReturnType<typeof transformTradePriceChange> = actions[
          signal.code
        ](signal.data);

        const contractIndex = chainDetails?.findIndex(
          (item) => item.isin === data.isin
        );

        if (data.isin === result.value.data.underlyingInstrumentIsin) {
          Object.assign(result.value.data, {
            underlyingAssetLastTradePrice: data.lastTradePrice,
          });
        }

        if (chainDetails && contractIndex && contractIndex !== -1) {
          const target = chainDetails[contractIndex];

          Object.entries(data).forEach(([key, value]) => {
            if (key in target) {
              target[key] = value;
            }
          });
        }
      }
    },
  });

  const contractsChain = computed(() =>
    result.value?.data
      ? new CalcSymbolValues(result.value.data, toValue(contractType)).calc()
      : undefined
  );

  return {
    contractsChain,
    getContractsChainStatus,
    getContractsChain,
    isLoadingContractsChain,
    isSuccessLoadingContractsChain,
    isErrorLoadingContractsChain,
  };
}
